import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Chip
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import TimelineIcon from '@mui/icons-material/Timeline';
import { getAccuracyTrend } from '../../utils/analyticsApi';

const AccuracyTrendChart = ({ scope, organizationId, teamId }) => {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrendData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, organizationId, teamId]);

  const fetchTrendData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { scope, limit: 20 };
      if (organizationId) params.organizationId = organizationId;
      if (teamId) params.teamId = teamId;
      const response = await getAccuracyTrend(params);
      setTrendData(response.data.trend || []);
    } catch (err) {
      console.error('Error fetching accuracy trend:', err);
      setError('Failed to load accuracy trend');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateLong = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle2">{data.sessionName}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDateLong(data.date)}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 600 }}>
              Accuracy: {data.accuracy}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.accurateCount} of {data.itemCount} items exact
            </Typography>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 70) return '#10B981';
    if (accuracy >= 50) return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Accuracy Over Time
        </Typography>
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (trendData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <TimelineIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No accuracy trend data available
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          Complete sessions with actual points entered in retros to see trends
        </Typography>
      </Box>
    );
  }

  // Calculate average for reference line
  const avgAccuracy = Math.round(
    trendData.reduce((sum, d) => sum + d.accuracy, 0) / trendData.length
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Accuracy Over Time
          </Typography>
          <Typography variant="body2" color="text.secondary">
            How estimation accuracy has changed across sessions
          </Typography>
        </Box>
        <Chip
          label={`Avg: ${avgAccuracy}%`}
          size="small"
          sx={{
            bgcolor: getAccuracyColor(avgAccuracy) + '20',
            color: getAccuracyColor(avgAccuracy),
            fontWeight: 600
          }}
        />
      </Box>

      <Paper sx={{ p: 2 }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#64748B', fontSize: 12 }}
              axisLine={{ stroke: '#CBD5E1' }}
              tickFormatter={formatDate}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: '#64748B', fontSize: 12 }}
              axisLine={{ stroke: '#CBD5E1' }}
              tickFormatter={(value) => `${value}%`}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={avgAccuracy}
              stroke="#9CA3AF"
              strokeDasharray="5 5"
              label={{
                value: `Avg ${avgAccuracy}%`,
                fill: '#9CA3AF',
                fontSize: 11,
                position: 'right'
              }}
            />
            <ReferenceLine
              y={70}
              stroke="#10B981"
              strokeDasharray="3 3"
              strokeOpacity={0.5}
            />
            <Line
              type="monotone"
              dataKey="accuracy"
              name="Accuracy"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={{ r: 4, fill: '#3B82F6' }}
              activeDot={{ r: 6, fill: '#3B82F6' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Session list */}
      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {trendData.slice(-5).reverse().map((session) => (
          <Chip
            key={session.sessionId}
            label={`${session.sessionName}: ${session.accuracy}%`}
            size="small"
            variant="outlined"
            sx={{
              borderColor: getAccuracyColor(session.accuracy),
              color: getAccuracyColor(session.accuracy)
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AccuracyTrendChart;
